import { useEditor } from "@grapesjs/react";
import {
  BootstrapSizes,
  Box,
  Button,
  ButtonColors,
  Checkbox,
  LightTable,
  Modal,
} from "@talentech/components";
import { useEffect, useState, useMemo } from "react";
import { CustomCommands } from "@/shared/utils";
import fonts from "../../../fonts";
import { PropertyProps } from "grapesjs";
import { generateGoogleFontsUrl } from "../utils/fontUtils";

interface Font {
  family: string;
  variants: string[];
  subsets: string[];
}

export interface FontProperty extends PropertyProps {
  getOptions: () => FontOption[];
  setOptions: (options: FontOption[]) => void;
}

export interface FontOption {
  id: string;
  label: string;
}

export default function FontSelectorModal() {
  const editor = useEditor();
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedFont, setSelectedFont] = useState<Font | null>(null);
  const [selectedSubsets, setSelectedSubsets] = useState<string[]>(["latin"]);
  const [selectedVariants, setSelectedVariants] = useState<string[]>([]);
  const [addedFonts, setAddedFonts] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [fontsPerPage] = useState(20);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // Reset to first page when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    editor.Commands.add(CustomCommands.MODAL_FONT_SELECTOR_OPEN, () => {
      setShowModal(true);
      setSelectedFont(null);
      setSelectedSubsets(["latin"]);
      setSelectedVariants([]);
      setSearch("");
      setDebouncedSearch("");
      setCurrentPage(1);

      // Load added fonts
      const ds = editor.DataSources.get("custom-fonts");
      if (ds) {
        const added = new Set(ds.getRecords().map((r) => r.get("family")));
        setAddedFonts(added);
      }
    });

    editor.Commands.add(CustomCommands.MODAL_FONT_SELECTOR_CLOSE, () => {
      setShowModal(false);
    });

    editor.Commands.add(CustomCommands.GET_FONT_LINKS, () => {
      const ds = editor.DataSources.get("custom-fonts");
      if (!ds) return [];
      return ds.getRecords().map((r) => r.get("url")).filter((url) => !!url);
    });
  }, [editor]);

  // Memoized filtered and paginated fonts for performance
  const { filteredFonts, totalPages, paginatedFonts } = useMemo(() => {
    const filtered = fonts.filter((font) =>
      font.family.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
    
    const total = Math.ceil(filtered.length / fontsPerPage);
    const startIndex = (currentPage - 1) * fontsPerPage;
    const endIndex = startIndex + fontsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);
    
    return {
      filteredFonts: filtered,
      totalPages: total,
      paginatedFonts: paginated
    };
  }, [debouncedSearch, currentPage, fontsPerPage]);

  const handleSelectFont = (font: Font) => {
    setSelectedFont(font);

    const ds = editor.DataSources.get("custom-fonts");
    if (ds) {
      const record = ds
        .getRecords()
        .find((r) => r.get("family") === font.family);
      if (record) {
        const subsets = String(record.get("subsets") || "").split(",").filter(Boolean);
        const variants = String(record.get("variants") || "").split(",").filter(Boolean);
        setSelectedSubsets(subsets.length ? subsets : ["latin"]);
        setSelectedVariants(variants);
        return;
      }
    }

    setSelectedSubsets(["latin"]);
    setSelectedVariants([]);
  };

  const toggleSubset = (subset: string) => {
    if (subset === "latin") return; // cannot unselect latin
    setSelectedSubsets((prev) =>
      prev.includes(subset)
        ? prev.filter((s) => s !== subset)
        : [...prev, subset]
    );
  };

  const toggleVariant = (variant: string) => {
    setSelectedVariants((prev) =>
      prev.includes(variant)
        ? prev.filter((v) => v !== variant)
        : [...prev, variant]
    );
  };

  const handleSave = () => {
    if (!selectedFont) return;

    const family = selectedFont.family;
    const url = generateGoogleFontsUrl(
      family,
      selectedVariants,
      selectedSubsets
    );

    // Add link to head (safe, idempotent)
    const doc = editor.Canvas.getDocument();
    const insertLink = () => {
      const currentDoc = editor.Canvas.getDocument();
      if (!currentDoc || !currentDoc.head) return;
      const headEl = currentDoc.head;
      if (!headEl.querySelector(`link[href="${url}"]`)) {
        const linkEl = currentDoc.createElement("link");
        linkEl.setAttribute("rel", "stylesheet");
        linkEl.setAttribute("href", url);
        linkEl.setAttribute("data-managed", "custom-font");
        headEl.appendChild(linkEl);
      }
    };
    if (!doc || !doc.head) {
      const onceLoad = () => {
        insertLink();
        editor.off("canvas:frame:load", onceLoad);
      };
      editor.on("canvas:frame:load", onceLoad);
    } else {
      insertLink();
    }

    // Add to typography font-family options
    const fontProp = editor.StyleManager.getProperty(
      "typography",
      "font-family"
    );
    if (fontProp) {
      const options: FontOption[] = (
        fontProp as unknown as FontProperty
      ).getOptions();
      if (!options.some((opt) => opt.id.toLowerCase() === family.toLowerCase())) {
        const nextOptions = options.concat({ id: `${family}`, label: family });
        (fontProp as unknown as FontProperty).setOptions(nextOptions);
      }
    }

    // Store in DataSources
    let ds = editor.DataSources.get("custom-fonts");
    if (!ds) {
      ds = editor.DataSources.add({ id: "custom-fonts", records: [] });
    }
    const record = ds.getRecords().find((r) => r.get("family") === family);
    const fontData = {
      id: family.toLowerCase().replace(/ /g, "-"),
      family,
      url,
      subsets: selectedSubsets.join(","),
      variants: selectedVariants.join(","),
    };
    if (record) {
      record.set(fontData);
    } else {
      ds.addRecord(fontData);
    }

    editor.StyleManager.render();

    setShowModal(false);
  };

  const handleBackToList = () => {
    setSelectedFont(null);
    setSelectedSubsets(["latin"]);
    setSelectedVariants([]);
  };

  const handleRemove = (family: string) => {
    const ds = editor.DataSources.get("custom-fonts");
    if (ds) {
      const record = ds.getRecords().find((r) => r.get("family") === family);
      if (record) {
        const recordId = record.get("id") as string;
        if (recordId) {
          ds.removeRecord(recordId);
        }

        // Remove from head
        const url = record.get("url");
        const doc = editor.Canvas.getDocument();
        const tryRemove = () => {
          const currentDoc = editor.Canvas.getDocument();
          if (!currentDoc || !currentDoc.head) return;
          const link = currentDoc.head.querySelector(`link[href="${url}"]`);
          if (link) link.remove();
        };
        if (!doc || !doc.head) {
          const onceLoad = () => {
            tryRemove();
            editor.off("canvas:frame:load", onceLoad);
          };
          editor.on("canvas:frame:load", onceLoad);
        } else {
          tryRemove();
        }

        // Remove from font options
        const fontProp = editor.StyleManager.getProperty(
          "typography",
          "font-family"
        );
        if (fontProp) {
          const options: FontOption[] = (
            fontProp as unknown as FontProperty
          ).getOptions();
          const index = options.findIndex((opt) => opt.id.toLowerCase() === family.toLowerCase());
          if (index !== -1) {
            const next = options.slice(0, index).concat(options.slice(index + 1));
            (fontProp as unknown as FontProperty).setOptions(next);
          }
        }

        // Refresh UI
        editor.StyleManager.render();

        // Update local state
        setAddedFonts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(family);
          return newSet;
        });

        if (selectedFont?.family === family) {
          setSelectedFont(null);
          setSelectedSubsets(["latin"]);
          setSelectedVariants([]);
        }
      }
    }
  };

  return (
    <Modal
      title="Select Font"
      showModal={showModal}
      onClose={() =>
        editor.runCommand(CustomCommands.MODAL_FONT_SELECTOR_CLOSE)
      }
      buttons={[
        <Button
          key="cancel"
          label="Cancel"
          onClick={() =>
            editor.runCommand(CustomCommands.MODAL_FONT_SELECTOR_CLOSE)
          }
          outline
        />,
        <Button
          key="save"
          label="Save"
          color={ButtonColors.PRIMARY}
          onClick={handleSave}
          disabled={!selectedFont || !selectedVariants.length}
        />,
      ]}
    >
      <Box className="d-flex gap-3" style={{ maxHeight: "600px" }}>
        {/* Left panel - Font list */}
        <Box className="d-flex flex-column flex-fill">
          <input
            placeholder="Search fonts..."
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
            className="form-control mb-2"
          />
          
          {/* Results info */}
          <div className="mb-2 text-muted small">
            {debouncedSearch ? 
              `Found ${filteredFonts.length} fonts matching "${debouncedSearch}"` :
              `Showing ${fontsPerPage} of ${fonts.length} fonts`
            }
          </div>
          
          {/* Font table */}
          <Box className="flex-grow-1 overflow-auto" style={{ minHeight: "300px" }}>
            <LightTable
              data={paginatedFonts}
              columns={[
                {
                  label: "Font Family",
                  accessor: "family",
                  Component: ({ family }: { family: string }) => (
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <span>
                          {family}
                        </span>
                        {addedFonts.has(family) && (
                          <span className="badge bg-success ms-2">Added</span>
                        )}
                      </div>
                      {addedFonts.has(family) && (
                        <Button
                          label="Remove"
                          icon={<i className="fa-sharp fa-trash me-1" />}
                          size={BootstrapSizes.SMALL}
                          color={ButtonColors.DANGER}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemove(family);
                          }}
                        />
                      )}
                    </div>
                  ),
                },
              ]}
              onRowClick={(row) => handleSelectFont(row)}
            />
          </Box>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Box className="d-flex justify-content-between align-items-center mt-3">
              <Button
                label="Previous"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                outline
              />
              <span className="text-muted">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                label="Next"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                outline
              />
            </Box>
          )}
        </Box>
        
        {/* Right panel - Font configuration */}
        {selectedFont && (
          <Box className="d-flex flex-column flex-grow-1 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                {selectedFont.family}
              </h5>
              <Button
                label="Back"
                size={BootstrapSizes.SMALL}
                onClick={handleBackToList}
                outline
              />
            </div>
            
            <div className="mb-3">
              <h6 className="mb-2">Subsets</h6>
              <div className="d-flex flex-column gap-1 border rounded p-2 overflow-auto" style={{ maxHeight: "120px" }}>
                {selectedFont.subsets.map((subset) => (
                  <Checkbox
                    key={subset}
                    id={`subset-${subset}`}
                    label={subset}
                    checked={selectedSubsets.includes(subset)}
                    onChange={() => toggleSubset(subset)}
                    disabled={subset === "latin"}
                  />
                ))}
              </div>
            </div>
            
            <div className="mb-3 flex-grow-1">
              <h6 className="mb-2">Variants</h6>
              <div className="d-flex flex-column gap-1 border rounded p-2 overflow-auto" style={{ maxHeight: "180px" }}>
                {selectedFont.variants.map((variant) => (
                  <Checkbox
                    key={variant}
                    id={`variant-${variant}`}
                    label={variant}
                    checked={selectedVariants.includes(variant)}
                    onChange={() => toggleVariant(variant)}
                  />
                ))}
              </div>
            </div>
            

          </Box>
        )}
      </Box>
    </Modal>
  );
}
